/**
 * Room Page
 *
 * View room details and available services
 */

import { resolveApiBaseUrl, SourceType } from '@polygraf/shared'
import {
	ArrowLeft,
	Building,
	Check,
	Copy,
	Edit2,
	FileText,
	Key,
	Loader2,
	Monitor,
	Play,
	Plus,
	Trash2,
	Video,
	X,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { QRCodeButton } from '@/components/qr-code'
import { EditRoomModal } from '@/components/room-edit-modal'
import { RoomMembers } from '@/components/room-members'
import { AddServiceModal, DeleteServiceModal, EditServiceModal } from '@/components/service-modals'
import { buttonClasses, cn } from '@/components/ui'
import { useAuthToken } from '@/hooks/use-auth-token'
import { usePageTitle } from '@/hooks/use-page-title'
import { useSession } from '@/lib/auth-client'
import { viewPathForService } from '@/lib/view-path'
import { trpc } from '@/trpc/client'

export default function RoomPage() {
	const { t } = useTranslation()
	const { spaceId, roomId } = useParams<{ spaceId: string; roomId: string }>()
	const navigate = useNavigate()
	const { data: session } = useSession()
	const [showAddService, setShowAddService] = useState(false)
	const [showEditRoom, setShowEditRoom] = useState(false)
	const utils = trpc.useUtils()

	// Generate shareable URL for this room
	const roomUrl = `${window.location.origin}/spaces/${spaceId}/rooms/${roomId}`

	const {
		data: room,
		isLoading,
		error,
	} = trpc.rooms.get.useQuery({ id: roomId ?? '' }, { enabled: !!roomId, refetchInterval: 5_000 })
	usePageTitle(room?.name ?? t('rooms.title'))

	if (isLoading) {
		return (
			<div className="flex justify-center py-12">
				<div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
			</div>
		)
	}

	if (error || !room) {
		return (
			<div className="space-y-4 animate-enter">
				<Link
					to={`/spaces/${spaceId}`}
					className={cn(
						buttonClasses({ variant: 'ghost', size: 'sm' }),
						'flex items-center gap-2 w-fit',
					)}
				>
					<ArrowLeft size={18} />
					{t('common.back')}
				</Link>
				<div className="card-refined p-6 border-coral-500 bg-coral-500/5">
					<p className="text-coral-700">{t('rooms.notFound')}</p>
				</div>
			</div>
		)
	}

	return (
		<div className="space-y-6 animate-enter">
			{/* Header */}
			<div className="flex items-center gap-4">
				<Link
					to={`/spaces/${spaceId}`}
					className={cn(buttonClasses({ variant: 'ghost', size: 'sm' }), 'p-2 rounded-xl')}
					aria-label={t('common.back')}
				>
					<ArrowLeft size={20} />
				</Link>
				<div className="flex-1 min-w-0">
					{/* Parent-space breadcrumb (U8) — the space name comes from rooms.get.
					    MU 17. 9. 2026: this was `text-sm text-base-content/60`, i.e. 14px
					    at 4.08:1 over the page background (App uses `bg-base-200`, cream[200]
					    #f5f0e8) — fails WCAG 1.4.3 AA (4.5:1), and was reported as too small
					    as well. 16px at /80 measures 7.62:1 light / 10.09:1 dark. */}
					<Link
						to={`/spaces/${spaceId}`}
						className="text-base font-medium text-base-content/80 hover:text-primary hover:underline"
					>
						{room.spaceName}
					</Link>
					<div className="flex flex-wrap items-center gap-3">
						<h1 className="text-2xl font-bold flex items-center gap-2">
							{/* Same icon as the room tiles on the space page (MU: the two
							    surfaces used different icons — "panelák" vs "domeček"). */}
							<Building size={24} className="text-primary flex-shrink-0" aria-hidden="true" />
							{room.name}
						</h1>
						{room.canManage && (
							<button
								type="button"
								onClick={() => setShowEditRoom(true)}
								className={cn(buttonClasses({ variant: 'ghost', size: 'sm' }), 'p-2 rounded-lg')}
								aria-label={t('spaceDetail.modal.editRoom')}
								title={t('spaceDetail.modal.editRoom')}
							>
								<Edit2 size={16} />
							</button>
						)}
						<QRCodeButton
							value={roomUrl}
							title={room.name}
							description={t('rooms.scanToAccess')}
							variant="ghost"
							size="sm"
							showLabel
						/>
					</div>
					{room.description && <p className="text-base-content/60 mt-1">{room.description}</p>}
				</div>
			</div>

			{/* Services */}
			<div className="space-y-4">
				<div className="flex items-center justify-between">
					<h2 className="text-lg font-semibold">{t('rooms.availableServices')}</h2>
					{session?.user && room.canManage && (
						<button
							type="button"
							className={cn(
								buttonClasses({ variant: 'ghost', size: 'sm' }),
								'flex items-center gap-2 text-sm',
							)}
							onClick={() => setShowAddService(true)}
						>
							<Plus size={16} />
							{t('rooms.addService')}
						</button>
					)}
				</div>

				{room.services.length === 0 ? (
					<div className="card-refined p-12 text-center">
						<p className="text-base-content/60">{t('rooms.noServices')}</p>
					</div>
				) : (
					<div className="grid gap-4 md:grid-cols-2">
						{room.services.map((service, index) => (
							<ServiceCard
								key={service.id}
								service={service}
								roomId={roomId ?? ''}
								isAuthenticated={!!session?.user}
								canManage={room.canManage}
								canPresent={room.canPresent}
								onNavigate={navigate}
								index={index}
								t={t}
							/>
						))}
					</div>
				)}
			</div>

			{/* Room access & members (managers only — UAT B3/F2, MP-217). An
			    inheriting room has no roster of its own; the component then shows
			    where access is managed instead. */}
			{room.canManageMembers && (
				<RoomMembers
					roomId={room.id}
					spaceId={room.spaceId}
					inheritAccess={room.inheritAccess}
					spaceVisibility={room.spaceVisibility}
				/>
			)}

			{/* Add Service Modal */}
			{showAddService && roomId && (
				<AddServiceModal
					roomId={roomId}
					onClose={() => setShowAddService(false)}
					onSuccess={() => setShowAddService(false)}
				/>
			)}

			{/* Edit room parameters — same modal as the space page's room tiles
			    (MU: pencil next to the room name). */}
			{showEditRoom && (
				<EditRoomModal
					room={room}
					onClose={() => setShowEditRoom(false)}
					onSuccess={() => {
						utils.rooms.get.invalidate({ id: room.id })
						setShowEditRoom(false)
					}}
					t={t}
				/>
			)}
		</div>
	)
}

function formatPairingCode(code: string): string {
	return `${code.slice(0, 3)}-${code.slice(3)}`
}

/** Mirrors PAIRING_CODE_TTL_SECONDS in packages/server/src/routes/writer-api.ts. */
const PAIRING_CODE_TTL_MS = 5 * 60 * 1000

function formatCountdown(ms: number): string {
	const totalSeconds = Math.max(0, Math.ceil(ms / 1000))
	const minutes = Math.floor(totalSeconds / 60)
	const seconds = totalSeconds % 60
	return `${minutes}:${String(seconds).padStart(2, '0')}`
}

function ServiceCard({
	service,
	roomId,
	isAuthenticated,
	canManage,
	canPresent,
	onNavigate,
	index,
	t,
}: {
	service: {
		id: string
		type: SourceType
		name: string
		endpoint?: string | null
		format?: string | null
		isActive: boolean
		isLive?: boolean
		/** Content is arriving right now; a subset of `isLive` (see the badge). */
		isPublishing?: boolean
		liveDocumentId?: string | null
	}
	roomId: string
	isAuthenticated: boolean
	canManage: boolean
	/** May push content (pairing code, test transcript, broadcast, capture) —
	 * wider than `canManage`: also space/room EDITORs (MU feedback 2026-07). */
	canPresent: boolean
	onNavigate: (path: string) => void
	index: number
	t: (key: string, options?: Record<string, unknown>) => string
}) {
	const isTranscript = service.type === SourceType.TRANSCRIPT
	// "Prezentační obraz" — a single service type to the user (MU 2026-07-24
	// #6). Covers a live browser share plus every image protocol (JPEG/MJPEG/
	// RTSP/NDI/HLS/MP4). Legacy SCREENCAST rows render here too; the retired
	// background-capture controls are gone (#9). Only a browser share (a SCREEN
	// with no static format = LiveKit) needs an explicit "start" — the other
	// protocols stream on-demand through the proxy, so no capture control.
	const isBrowserShare = service.type === SourceType.SCREEN && !service.format

	// Canonical transcript/screen/session routing — shared with the source
	// selector via viewPathForService so the two can't drift again.
	const viewPath = viewPathForService(service)

	// Generate full URL for QR code
	const serviceUrl = `${window.location.origin}${viewPath}`

	// Format display name. A browser share is a SCREEN with no static format,
	// which used to mean NO badge at all while JPG/MJPG/RTSP tiles had one
	// (MU 5. 8.) — label it with the same term the creation dialog uses.
	const formatLabel = service.format ? service.format.toUpperCase() : null

	// Pairing code state
	const token = useAuthToken()
	const [pairingCode, setPairingCode] = useState<string | null>(null)
	const [isGenerating, setIsGenerating] = useState(false)
	const [codeError, setCodeError] = useState<string | null>(null)
	const [copiedCode, setCopiedCode] = useState(false)
	const [copiedServer, setCopiedServer] = useState(false)

	// The code is single-use with a 5-minute server-side TTL; count down locally
	// from the moment it was issued so the panel never advertises a dead code.
	const [codeExpiresAt, setCodeExpiresAt] = useState<number | null>(null)
	const [nowMs, setNowMs] = useState(() => Date.now())
	const remainingMs = codeExpiresAt === null ? null : Math.max(0, codeExpiresAt - nowMs)
	const codeExpired = remainingMs === 0

	// Tick once a second while a code is live; the effect re-runs and clears the
	// interval when the countdown hits zero (or a new code resets `codeExpiresAt`).
	useEffect(() => {
		if (codeExpiresAt === null || codeExpired) return
		const timer = setInterval(() => setNowMs(Date.now()), 1_000)
		return () => clearInterval(timer)
	}, [codeExpiresAt, codeExpired])

	// MP-153: the writer-pairing flow (create-code POST + the server URL shown to
	// the stenographer) must target the API origin, not the web origin. In prod
	// the SPA host (polygraf.app) and API host (api.polygraf.app) are split, so
	// `window.location.origin` made the create-code POST hit the static SPA, which
	// returns an HTML 405 — the client then choked on it with "Unexpected token
	// '<'". Same resolver every other API call uses; falls back to the web origin
	// in dev where the Vite proxy serves /api on the same origin.
	const serverUrl = resolveApiBaseUrl(import.meta.env.VITE_API_URL, window.location.origin)

	// Start web transcript session (reuses existing session for this room if one
	// exists). `serviceId` rides along so the writer page's `liveSessions.ensure`
	// re-links the session to this service after a reload — the WS server
	// deletes the row on writer disconnect, and an unlinked re-create made the
	// tile read "nevysílá" while the writer streamed (MU 2026-09-04, N2).
	const createSessionMutation = trpc.liveSessions.findOrCreate.useMutation({
		onSuccess: (data, variables) => {
			const params = new URLSearchParams({
				documentId: data.documentId,
				title: data.title,
				roomId,
			})
			if (variables.serviceId) params.set('serviceId', variables.serviceId)
			onNavigate(`/writer?${params.toString()}`)
		},
	})

	// Start web broadcast session (reuses existing session for this room if one
	// exists). Routes to the LiveKit presenter: `findOrCreate` already records the
	// caller as a BROADCASTER participant and sets `hasVideo`, so the GET token
	// route issues a publisher token for this session. Replaces the retired
	// frames-based `/broadcaster` page (which pushed JPEG frames that nothing
	// rendered — see the LiveKit screen-share migration).
	const createBroadcastMutation = trpc.liveSessions.findOrCreate.useMutation({
		onSuccess: (data) => {
			onNavigate(`/sessions/${data.id}/present`)
		},
	})

	// Edit / delete a service (manager-only). The modals live in
	// components/service-modals.tsx, shared with the space-detail room cards.
	const [showEdit, setShowEdit] = useState(false)
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

	const handleGenerateCode = async () => {
		if (!token) return
		setIsGenerating(true)
		setCodeError(null)
		setPairingCode(null)
		setCodeExpiresAt(null)

		try {
			const res = await fetch(`${serverUrl}/api/writer/create-code`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${token}`,
				},
				body: JSON.stringify({ serviceId: service.id }),
			})

			const data = await res.json()

			if (!res.ok) {
				throw new Error(data.error ?? t('rooms.generateCodeFailed'))
			}

			setPairingCode(data.code)
			setNowMs(Date.now())
			setCodeExpiresAt(Date.now() + PAIRING_CODE_TTL_MS)
		} catch (err) {
			setCodeError(err instanceof Error ? err.message : t('rooms.generateCodeFailed'))
		} finally {
			setIsGenerating(false)
		}
	}

	// U11 (MP-219): the panel used to be un-dismissable — `pairingCode` was only
	// ever set, never cleared while open. Hiding also drops the countdown state.
	const hideCode = () => {
		setPairingCode(null)
		setCodeExpiresAt(null)
	}

	const handleToggleCode = () => {
		if (pairingCode) {
			hideCode()
		} else {
			void handleGenerateCode()
		}
	}

	const handleCopyCode = async () => {
		if (!pairingCode) return
		try {
			await navigator.clipboard.writeText(pairingCode)
			setCopiedCode(true)
			setTimeout(() => setCopiedCode(false), 2000)
		} catch {
			/* ignore */
		}
	}

	const handleCopyServer = async () => {
		try {
			await navigator.clipboard.writeText(serverUrl)
			setCopiedServer(true)
			setTimeout(() => setCopiedServer(false), 2000)
		} catch {
			/* ignore */
		}
	}

	return (
		<div className={`card-refined p-5 animate-enter stagger-${Math.min(index + 1, 5)}`}>
			{/* Header: icon, name, badges */}
			<div className="flex items-center gap-3">
				<div
					className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
						isTranscript ? 'bg-primary/10' : 'bg-sage-500/10'
					}`}
				>
					{isTranscript ? (
						<FileText size={20} className="text-primary" aria-hidden="true" />
					) : (
						<Monitor size={20} className="text-sage-600" aria-hidden="true" />
					)}
				</div>
				<div className="flex-1 min-w-0">
					<h4 className="font-semibold break-words">{service.name}</h4>
					{/* Not the reported overflow (that is the pairing panel below) — the
					    type line measured clean at 320px with today's Czech strings.
					    But it is one non-wrapping row of label + badge, so a longer
					    format label would squeeze or push it; let it wrap instead. */}
					<div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-base-content/60">
						<span>{isTranscript ? t('rooms.transcript') : t('rooms.screen')}</span>
						{(formatLabel || isBrowserShare) && (
							<span className="px-2 py-0.5 text-xs rounded-full bg-base-200 text-base-content/70 max-w-full break-words">
								{formatLabel ?? t('rooms.screenLiveShare')}
							</span>
						)}
					</div>
				</div>
				<div className="flex items-center gap-2 flex-shrink-0">
					{/* Three states, not two (MU 2026-09-04 / 2026-09-17). `isLive` means a
					    transcriber/broadcaster is attached; `isPublishing` means content is
					    arriving. The middle case — paired but not typing — used to be
					    indistinguishable from nobody being there at all, so the tile read
					    "nevysílá" at a transcriber who was sitting right there. */}
					{service.isPublishing ? (
						<div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-error/10 text-coral-700 text-xs font-medium">
							<span
								className="w-1.5 h-1.5 rounded-full bg-error animate-pulse"
								aria-hidden="true"
							/>
							{t('rooms.live')}
						</div>
					) : service.isLive ? (
						<div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-base-200 text-base-content/70 text-xs font-medium">
							<span className="w-1.5 h-1.5 rounded-full bg-base-content/40" aria-hidden="true" />
							{t('rooms.connectedNotTyping')}
						</div>
					) : null}
					{/* Reserved for "configured but offline" — the green "Aktivní" badge
					    used to render here, but it implied something was streaming when
					    the writer/broadcaster wasn't actually online (Sváťa Apr 11
					    feedback). LIVE alone now signals an actual stream. */}
					{canManage && (
						<>
							<button
								type="button"
								onClick={() => setShowEdit(true)}
								className={cn(buttonClasses({ variant: 'ghost', size: 'sm' }), 'p-2 rounded-lg')}
								aria-label={t('rooms.editService')}
								title={t('rooms.editService')}
							>
								<Edit2 size={16} />
							</button>
							<button
								type="button"
								onClick={() => setShowDeleteConfirm(true)}
								className={cn(
									buttonClasses({ variant: 'ghost', size: 'sm' }),
									'p-2 rounded-lg text-coral-700',
								)}
								aria-label={t('rooms.deleteService')}
								title={t('rooms.deleteService')}
							>
								<Trash2 size={16} />
							</button>
						</>
					)}
				</div>
			</div>

			{/* Divider */}
			<div className="border-t border-base-200 my-4" />

			{/* Actions: primary on left, utilities on right */}
			<div className="flex flex-wrap items-center justify-between gap-3">
				<div className="flex items-center gap-2 flex-wrap min-w-0">
					<Link to={viewPath} className={cn(buttonClasses(), 'flex items-center gap-1.5 text-sm')}>
						<Play size={14} />
						{t('rooms.watch')}
					</Link>
					{isTranscript && isAuthenticated && canPresent && (
						<button
							type="button"
							className={cn(
								buttonClasses({ variant: 'accent' }),
								'flex items-center gap-1.5 text-sm',
							)}
							disabled={createSessionMutation.isPending}
							onClick={() =>
								createSessionMutation.mutate({
									title: service.name,
									role: 'WRITER',
									roomId,
									serviceId: service.id,
								})
							}
						>
							{createSessionMutation.isPending ? (
								<Loader2 size={14} className="animate-spin" />
							) : (
								<Play size={14} />
							)}
							{t('rooms.startTestWebTranscript')}
						</button>
					)}
					{isTranscript && canPresent && (
						<button
							type="button"
							className={cn(
								buttonClasses({ variant: 'ghost', size: 'sm' }),
								'flex items-center gap-1.5 text-sm border border-base-300',
							)}
							onClick={handleToggleCode}
							// Hiding an already-visible code must always work; only
							// generating a new one needs a token.
							disabled={isGenerating || (!pairingCode && !token)}
							aria-expanded={!!pairingCode}
						>
							{isGenerating ? <Loader2 size={14} className="animate-spin" /> : <Key size={14} />}
							{pairingCode ? t('rooms.hideCode') : t('rooms.writerCode')}
						</button>
					)}
					{isBrowserShare && isAuthenticated && canPresent && (
						<button
							type="button"
							className={cn(
								buttonClasses({ variant: 'accent' }),
								'flex items-center gap-1.5 text-sm',
							)}
							disabled={createBroadcastMutation.isPending}
							onClick={() =>
								createBroadcastMutation.mutate({
									title: service.name,
									role: 'BROADCASTER',
									roomId,
									serviceId: service.id,
								})
							}
						>
							{createBroadcastMutation.isPending ? (
								<Loader2 size={14} className="animate-spin" />
							) : (
								<Video size={14} />
							)}
							{t('rooms.startWebBroadcast')}
						</button>
					)}
				</div>
				<div className="flex-shrink-0">
					<QRCodeButton
						value={serviceUrl}
						title={service.name}
						description={isTranscript ? t('rooms.scanForTranscript') : t('rooms.scanForScreen')}
						variant="ghost"
						size="sm"
						showLabel
					/>
				</div>
			</div>

			{/* Error messages */}
			{createSessionMutation.error && (
				<div role="alert" className="text-sm text-coral-700 bg-error/10 px-3 py-2 rounded-lg mt-3">
					{createSessionMutation.error.message}
				</div>
			)}

			{createBroadcastMutation.error && (
				<div role="alert" className="text-sm text-coral-700 bg-error/10 px-3 py-2 rounded-lg mt-3">
					{createBroadcastMutation.error.message}
				</div>
			)}

			{codeError && (
				<div role="alert" className="text-sm text-coral-700 bg-error/10 px-3 py-2 rounded-lg mt-3">
					{codeError}
				</div>
			)}

			{/* Pairing code panel */}
			{pairingCode && (
				<div className="mt-4 p-4 rounded-xl bg-base-200 space-y-3">
					<div className="flex items-center justify-between">
						<h5 className="font-semibold text-sm">{t('rooms.writerPairingCode')}</h5>
						<button
							type="button"
							onClick={hideCode}
							className={cn(buttonClasses({ variant: 'ghost', size: 'sm' }), 'p-1 rounded-lg')}
							aria-label={t('rooms.hideCode')}
							title={t('rooms.hideCode')}
						>
							<X size={14} aria-hidden="true" />
						</button>
					</div>

					{/* QA asked twice what the code is and where it goes (MP-202,
					    MP-99): the panel was four labels and two copy buttons. */}
					<p className="text-xs text-base-content/70">{t('rooms.pairingHint')}</p>

					{/* This is the block that overflows (MU 17. 9.). `serverUrl` is the
					    API origin, and prod's "https://api.polygraf.app" has no hyphen or
					    slash to break at, so the whole string is one token: in a
					    non-wrapping `justify-between` row its min-content width sets the
					    row width and the panel pushes past the card edge (measured +4px
					    at 320px; a path-bearing URL overflows by ~150px). The testing
					    origin "polygraf-api.blaze.codes" happens to fold at its hyphens,
					    which is why the bug shows up on some deployments and not others.
					    `break-all` + a wrapping row lets it fold instead of pushing. */}
					<div className="space-y-2">
						<div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-1">
							<span className="text-xs text-base-content/60">{t('rooms.pairingCodeLabel')}</span>
							<span
								className={`font-mono text-lg font-bold tracking-wider min-w-0 break-all ${
									codeExpired ? 'line-through opacity-40' : ''
								}`}
							>
								{formatPairingCode(pairingCode)}
							</span>
						</div>
						<div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-1">
							<span className="text-xs text-base-content/60">{t('rooms.pairingServerLabel')}</span>
							<span className="font-mono text-sm min-w-0 break-all">{serverUrl}</span>
						</div>
					</div>

					{/* Live mm:ss countdown to the server-side TTL; when it reaches zero
					    the code is dead and the "Writer code" button above regenerates.
					    Only the expired state is a status live region — announcing every
					    countdown tick would spam screen readers. */}
					{codeExpired ? (
						<output className="block text-xs font-medium text-coral-700">
							{t('rooms.pairingExpired')}
						</output>
					) : (
						<p className="text-xs text-base-content/40">
							{t('rooms.pairingExpiresIn', { time: formatCountdown(remainingMs ?? 0) })}
						</p>
					)}

					<div className="flex flex-wrap gap-2">
						<button
							type="button"
							className={cn(
								buttonClasses({ variant: 'ghost', size: 'sm' }),
								'text-xs px-3 py-1.5 flex items-center gap-1 border border-base-300',
							)}
							onClick={handleCopyCode}
						>
							{copiedCode ? (
								<Check size={12} className="text-sage-500" aria-hidden="true" />
							) : (
								<Copy size={12} aria-hidden="true" />
							)}
							{copiedCode ? t('common.copied') : t('rooms.copyCode')}
						</button>
						<button
							type="button"
							className={cn(
								buttonClasses({ variant: 'ghost', size: 'sm' }),
								'text-xs px-3 py-1.5 flex items-center gap-1 border border-base-300',
							)}
							onClick={handleCopyServer}
							title={t('rooms.copyServerUrlHint')}
						>
							{copiedServer ? (
								<Check size={12} className="text-sage-500" aria-hidden="true" />
							) : (
								<Copy size={12} aria-hidden="true" />
							)}
							{copiedServer ? t('common.copied') : t('rooms.copyServerUrl')}
						</button>
					</div>
				</div>
			)}

			{/* Edit Service Modal (manager-only) */}
			{showEdit && (
				<EditServiceModal
					service={service}
					roomId={roomId}
					onClose={() => setShowEdit(false)}
					onSuccess={() => setShowEdit(false)}
				/>
			)}

			{/* Delete Service confirmation (manager-only) */}
			<DeleteServiceModal
				serviceId={service.id}
				roomId={roomId}
				open={showDeleteConfirm}
				onClose={() => setShowDeleteConfirm(false)}
				onSuccess={() => setShowDeleteConfirm(false)}
			/>
		</div>
	)
}
